import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {endOfDay, startOfDay, subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import ScrollTopButton from "../../../components/scroll-top-button/scroll-top-button.jsx";
import occurrenceMappingService from "../../../services/reports/occurrence-mapping-service.js";
import {useQuery} from "@tanstack/react-query";
import reportUtils from "../../../utils/report-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import Content from "./components/content.jsx";


const initialFilters = () => {
  const now = new Date();

  return {
    dtStart: subMonths(now, 1),
    dtEnd: now,
    period: {value: "all", label: "Todos"},
    hrStart: startOfDay(now),
    hrEnd: endOfDay(now),
    reason: [],
    status: [],
    cityFact: "",
    neighborhood: [],
    onlyMyCity: true,
    archivedOccurrences: true
  };
};

const filterLabels = {
  dtStart: "Data inicial",
  dtEnd: "Data final",
  reason: "Natureza",
  hrStart: "Hora inicial",
  hrEnd: "Hora final",
  status: "Status",
  neighborhood: "Bairro",
  cityFact: "Município do fato",
  archivedOccurrences: "Contabilizar ocorrências arquivadas",
  onlyMyCity: "Apenas ocorrências da minha corporação",
  period: "Período"
};

const buildPayload = (filters) => {
  return {
    dtStart: reportUtils.formatDateOnly(filters.dtStart),
    dtEnd: reportUtils.formatDateOnly(filters.dtEnd),
    hrStart: projectUtils.formatTimeString(filters.hrStart),
    hrEnd: projectUtils.formatTimeString(filters.hrEnd),
    reason: filters.reason.map(el => el.value),
    status: filters.status.map(el => el.value),
    cityFact: filters.cityFact,
    neighborhood: filters.neighborhood.map(el => el.value),
    ...(filters?.archivedOccurrences && { archivedOccurrences: "on" }),
    ...(filters?.onlyMyCity && { onlyMyCity: "on" }),
  };
}

const OccurrenceMapping = () => {
  const [loading, setLoading] = useState(false);

  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [searchVersion, setSearchVersion] = useState(0);

  const [title] = useState("Mapeamento de ocorrências");
  const [headerSelected, setHeaderSelected] = useState(-1);

  const {data: reportData = null, error, isFetching} = useQuery({
    queryKey: ["occurrence-mapping", searchVersion, appliedFilters],
    queryFn: () => occurrenceMappingService.apply(appliedFilters),
    enabled: !!appliedFilters
  });

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        period: ReportUtils.periods().find(el => el.value === filters.period.value).label,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
      }, filterLabels)
    );

  }, [reportData]);

  useEffect(() => {
    if (!error) return;

    ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
    console.error("Erro ao gerar o relatório", error);
  }, [error]);

  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setAppliedFilters(null)
      }
    });

    try {
      const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, "month");

      if (!periodValidation.ok) {
        ReportUtils.alertErrorMessage(periodValidation.message);
        setLoading(false);
        return;
      }

      const hourValidation = ReportUtils.validateTimeString(filters.hrStart, filters.hrEnd);

      if (!hourValidation.ok) {
        ReportUtils.alertErrorMessage(hourValidation.message);
        setLoading(false);
        return;
      }

      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
        hrStart: filters.hrStart ? projectUtils.formatTimeString(filters.hrStart) : null,
        hrEnd: filters.hrEnd ? projectUtils.formatTimeString(filters.hrEnd) : null,
        archivedOccurrences: filters.archivedOccurrences ? "Sim" : "Não",
        onlyMyCity: filters.onlyMyCity ? "Sim" : "Não",
      };

      const {period, ...rest} = filters;

      const auditFilters = ReportUtils.filters({
          ...rest,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
          hrStart: formattedFilters.hrStart,
          hrEnd: formattedFilters.hrEnd,
          archivedOccurrences: formattedFilters.archivedOccurrences,
          onlyMyCity: formattedFilters.onlyMyCity
        },
        filterLabels, true);

      setAppliedFilters({...buildPayload(filters), filters: auditFilters});
      setSearchVersion(prev => prev + 1);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setFilters(initialFilters);
    setAppliedFilters(null);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);
  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  const printComponentRef = useRef(null);

  return (
    <div className={"flex"}>

      <Template isLoading={loading || isFetching}>
        <Template.Header>
          <div className="flex justify-between w-full">
            <Template.Title>{title}</Template.Title>
            <HeaderButtons
              exportable={true}
              exportInSheets={true}
              printable={true}
              headerChange={true}
              printComponentRef={printComponentRef}
              reportData={reportData}
              data={reportData?.occMappingList.length ?? 0}
              headerSelected={headerSelected}
              setHeaderSelected={setHeaderSelected}
              setCollapsed={setCollapsed}
            />
          </div>
        </Template.Header>
        <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
          <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

            <Filters setFilters={setFilters} filters={filters}/>
            <AutoDatetime setFilters={setFilters}/>

          </Menu>
        </Template.Body>
      </Template>

      <Card className={`w-full h-fit mr-2 ${(!reportData || reportData?.occMappingList.length === 0) ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${(!reportData || reportData?.occMappingList.length === 0) ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
        <Card.Body>
          <div className={`flex-1 ${!reportData || reportData?.occMappingList.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
            <NotAppliedContent reportData={reportData}/>
            <NoDataContent reportData={reportData} data={reportData?.occMappingList.length ?? 0}/>
            <Content reportData={reportData} data={reportData?.occMappingList.length ?? 0} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
          </div>
        </Card.Body>
      </Card>

      <ScrollTopButton/>

    </div>
  );
};

export default OccurrenceMapping;
